import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Menubar } from 'primereact/menubar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import ObjectID from 'bson-objectid';
import { useSelector, useDispatch } from 'react-redux';
import {
  showEldDialog,
  showIntDialog,
  showCtyDialog,
  showLogDialog,
  showOthDialog,
  showEspDialog,
} from '../../redux/actions/dialogActions.js';
import {
  clearClient,
  clearComment,
  clearCty,
  clearDate,
  clearEsp,
  clearHour,
  clearModType,
  clearModule,
  clearStage,
  clearTask,
  clearWpl,
  setClient,
  setComment,
  setCty,
  setDate,
  setEsp,
  setHour,
  setModType,
  setModule,
  setStage,
  setTask,
  setWpl,
  setLang,
  clearLang,
} from '../../redux/actions/logActions';
import TableLegend from './utils/TableLegend.js';
import EspAccess from './utils/EspAccess.js';
import RowExpansionTemplate from './utils/RowExpansionTemplate.js';
import { dictionary } from '../../dictionary';
import { fetchInitialPosts, fetchEspPosts } from '../../redux/thunk/thunk';
import { expandAll, collapseAll } from './utils/utils.js';
import EspLogsTable from './EspLogsTable.js';
import MenubarContent from './utils/MenubarContent.js';
const isDevelopment = process.env.NODE_ENV === 'development';
const API_URL = isDevelopment
  ? process.env.REACT_APP_API_URL
  : 'https://eld-timer-backend-service.api.prod.sit.sys.odj.cloud/';

const UserLogsTable = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const contentProps = props.contentProps;
  const [recordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const userPosts = useSelector((state) => state.posts.data);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [forbiddenError, setForbiddenError] = useState(null);
  const username = sessionStorage.getItem('userName');
  const personType = sessionStorage.getItem('user') ? JSON.parse(sessionStorage.getItem('user')).person : null;
  const [tabKeys, setTabKeys] = useState(null);
  const [expandedRows, setExpandedRows] = useState(null);
  const [showClientList, setShowClientList] = useState(false);
  const [activeTab, setActiveTab] = useState('post');
  const [showEspTable, setShowEspTable] = useState(false);
  const userIsAdmin = sessionStorage.getItem('isAdmin') === 'true';
  const POST_TAB = 'post';
  const ESP_TAB = 'esp';
  const { start, end } = MenubarContent({
    dictionary,
    contentProps,
    activeTab,
    setActiveTab,
    setShowEspTable,
    userIsAdmin,
    navigate,
    POST_TAB,
    ESP_TAB,
  });

  useEffect(() => {
    if (showEspTable && userIsAdmin) {
      setLoading(true);
      dispatch(fetchEspPosts())
        .then(() => setLoading(false))
        .catch(() => setLoading(false));
    }
  }, [showEspTable, userIsAdmin, dispatch]);

  const onPage = (rowData) => {
    setCurrentPage(rowData.first / recordsPerPage);
  };

  useEffect(() => {
    const fetchTotalRecords = async () => {
      try {
        const response = await fetch(`${API_URL}posts/${username}/count`, {
          method: 'GET',
          mode: 'cors',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 403) {
          throw new Error('Forbidden');
        }

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const count = await response.json();
        if (!count.length) {
          setTotalRecords(0);
        } else {
          setTotalRecords(count[0].count);
        }
      } catch (error) {
        if (error.message === 'Failed to fetch') {
          console.error('Connection refused. Please check if the server is running.');
          setConnectionError('Connection refused. Please check if the server is running.');
        } else if (error.message === 'Forbidden') {
          console.error('Access denied. You do not have permission to access this resource.');
          setForbiddenError('Connection refused. Please check if the server is running.');
        } else {
          console.error('An error occurred:', error.message);
        }
      }
    };

    fetchTotalRecords();
  }, [username]);

  useEffect(() => {
    if (contentProps.eldData !== null) {
      setTabKeys(contentProps.eldData[0]?.perso);
    }
  }, [contentProps.eldData]);

  const issueClass = (rowData) => {
    let baseClass = '';

    switch (rowData.module) {
      case 'Urlop':
        baseClass = 'cir_0';
        break;
      case 'Szkolenia':
        baseClass = 'cir_1';
        break;
      case 'INT_Spotkania':
      case 'Spotkania':
        baseClass = 'cir_2';
        break;
      case 'Zwolnienie lekarskie':
        baseClass = 'cir_3';
        break;
      case 'Zwolnienie z obowiązku świadczenia pracy':
        baseClass = 'cir_4';
        break;
      case 'Opieka na dziecko':
        baseClass = 'cir_5';
        break;
      case 'Wcześniejsze wyjście':
        baseClass = 'cir_6';
        break;
      case 'Organizacja pracy':
        baseClass = 'cir_7';
        break;
      case 'Wolne moce':
        baseClass = 'cir_8';
        break;
      case 'Medycyna pracy':
        baseClass = 'cir_9';
        break;
      case 'Urlop macierzyński/ojcowski/tacierzyński':
        baseClass = 'cir_10';
        break;
      default:
        break;
    }

    if (baseClass === '') {
      switch (rowData.wpl) {
        case 'Biuro':
          baseClass = 'cir_11';
          break;
        case 'PZO/PZH':
          baseClass = 'cir_12';
          break;
        default:
          break;
      }
    }

    if (rowData.esp && rowData.esp !== '') {
      return baseClass + (baseClass ? ' ' : '') + 'esp-row';
    }
    return baseClass;
  };

  const handleClientChoice = (client) => {
    dispatch(setClient(client));
    contentProps.setPostEditing(true);
    handleOpenDialog(client);
    setShowClientList(false);
  };

  const handleOpenDialog = (cli) => {
    let client = cli.toLowerCase();
    switch (client) {
      case 'eld':
        dispatch(setClient('eld'));
        dispatch(showEldDialog());
        break;
      case 'int':
        dispatch(setClient('int'));
        dispatch(showIntDialog());
        break;
      case 'country':
        dispatch(setClient('country'));
        dispatch(showCtyDialog());
        break;
      case 'logistics':
        dispatch(setClient('logistics'));
        dispatch(showLogDialog());
        break;
      case 'other':
        dispatch(setClient('other'));
        dispatch(showOthDialog());
        break;
      case 'esp':
        dispatch(setClient('esp'));
        dispatch(showEspDialog());
        break;
      default:
        return null;
    }
  };

  const handleRowEdit = (rowData) => {
    if (rowData.client.toLowerCase() === '') {
      setShowClientList(true);
      let parts = rowData.date.split('-');
      const yyy = parseInt(parts[0]);
      const mm = parseInt(parts[1]) - 1;
      const dd = parseInt(parts[2]);
      const date = new Date(yyy, mm, dd).toDateString();
      rowData._id === '' ? contentProps.setEditId(null) : contentProps.setEditId(rowData._id);
      rowData.date === '' ? dispatch(clearDate()) : dispatch(setDate(date));
      rowData.module === '' ? dispatch(clearModule()) : dispatch(setModule(rowData.module));
      rowData.client === '' ? dispatch(clearClient()) : dispatch(setClient(rowData.client));
      rowData.country === '' ? dispatch(clearCty()) : dispatch(setCty(rowData.country));
      rowData.lang === '' ? dispatch(clearLang()) : dispatch(setLang(rowData.lang));
      rowData.module_type === '' ? dispatch(clearModType()) : dispatch(setModType(rowData.module_type));
      rowData.stage === '' ? dispatch(clearStage()) : dispatch(setStage(rowData.stage));
      rowData.hours === '' ? dispatch(clearHour()) : dispatch(setHour(rowData.hours));
      rowData.wpl === '' ? dispatch(clearWpl()) : dispatch(setWpl(rowData.wpl));
      rowData.comment === '' ? dispatch(clearComment()) : dispatch(setComment(rowData.comment));
      rowData.task === '' ? dispatch(clearTask()) : dispatch(setTask(rowData.task));
      rowData.esp === '' ? dispatch(clearEsp()) : dispatch(setEsp(rowData.esp));
    } else {
      contentProps.setPostEditing(true);
      let parts = rowData.date.split('-');
      const yyy = parseInt(parts[0]);
      const mm = parseInt(parts[1]) - 1;
      const dd = parseInt(parts[2]);
      const date = new Date(yyy, mm, dd).toDateString();
      handleOpenDialog(rowData.client);
      rowData._id === '' ? contentProps.setEditId(null) : contentProps.setEditId(rowData._id);
      rowData.date === '' ? dispatch(clearDate()) : dispatch(setDate(date));
      rowData.module === '' ? dispatch(clearModule()) : dispatch(setModule(rowData.module));
      rowData.client === '' ? dispatch(clearClient()) : dispatch(setClient(rowData.client));
      rowData.country === '' ? dispatch(clearCty()) : dispatch(setCty(rowData.country));
      rowData.lang === '' ? dispatch(clearLang()) : dispatch(setLang(rowData.lang));
      rowData.module_type === '' ? dispatch(clearModType()) : dispatch(setModType(rowData.module_type));
      rowData.stage === '' ? dispatch(clearStage()) : dispatch(setStage(rowData.stage));
      rowData.hours === '' ? dispatch(clearHour()) : dispatch(setHour(rowData.hours));
      rowData.wpl === '' ? dispatch(clearWpl()) : dispatch(setWpl(rowData.wpl));
      rowData.comment === '' ? dispatch(clearComment()) : dispatch(setComment(rowData.comment));
      rowData.task === '' ? dispatch(clearTask()) : dispatch(setTask(rowData.task));
      rowData.esp === '' ? dispatch(clearEsp()) : dispatch(setEsp(rowData.esp));
    }
  };

  const onRowCopy = (rowData) => {
    const log = {
      id: ObjectID().toHexString(),
      user: rowData.user,
      client: rowData.client,
      esp: rowData.esp,
      country: rowData.country,
      module_type: rowData.module_type,
      module: rowData.module,
      stage: rowData.stage,
      task: rowData.task,
      hours: rowData.hours,
      wpl: rowData.wpl,
      workingh: rowData.workingh,
      comment: rowData.comment,
      date: rowData.date,
      cw: rowData.cw,
    };
    fetch(`${API_URL}posts`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(log),
    }).then((response) => {
      if (response.ok) {
        dispatch(fetchInitialPosts(contentProps.loggedUser));
      }
    });
  };

  const header = (
    <div className="header-btn-wrapper">
      <TableLegend tabKeys={tabKeys} dictionary={dictionary} />
      <div className="expand-btn-wrapper">
        <Button
          icon="pi pi-plus"
          label={dictionary.user_logs_table_btn_label_expand}
          onClick={() => expandAll(userPosts, setExpandedRows)}
        />

        <Button
          icon="pi pi-minus"
          label={dictionary.user_logs_table_btn_label_collapse}
          onClick={() => collapseAll(setExpandedRows)}
        />
      </div>
    </div>
  );

  const onRowDelete = (itemId) => {
    fetch(`${API_URL}posts/${itemId}`, {
      method: 'DELETE',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.ok) {
          dispatch(fetchInitialPosts(contentProps.loggedUser));
        } else {
          console.error('An error occurred while deleting an item from the database!');
          return;
        }
      })
      .catch((error) => console.error('Error: ', error));
  };

  useEffect(() => {
    if (userPosts) {
      setLoading(false);
      setDataLoaded(true);
    }
  }, [userPosts]);

  return (
    <div className="post-table">
      <Menubar start={start} end={end} />
      <Dialog
        header={dictionary.user_logs_table_dialog_client}
        visible={showClientList}
        style={{ width: '50vw' }}
        onHide={() => {
          setShowClientList(false);
        }}
      >
        <div className="post-table-dialog-btn-wrapper">
          <Button
            icon="pi pi-external-link"
            label={dictionary.user_logs_table_dialog_client_btn_label1}
            onClick={() => handleClientChoice('ELD')}
          />
          <Button
            icon="pi pi-external-link"
            label={dictionary.user_logs_table_dialog_client_btn_label2}
            onClick={() => handleClientChoice('INT')}
          />
          <Button
            icon="pi pi-external-link"
            label={dictionary.user_logs_table_dialog_client_btn_label3}
            onClick={() => handleClientChoice('COUNTRY')}
          />
          <Button
            icon="pi pi-external-link"
            label={dictionary.user_logs_table_dialog_client_btn_label4}
            onClick={() => handleClientChoice('LOGISTICS')}
          />
          <Button
            icon="pi pi-external-link"
            label={dictionary.user_logs_table_dialog_client_btn_label5}
            onClick={() => handleClientChoice('OTHER')}
          />
        </div>
      </Dialog>
      {loading ? (
        <div className="loading-wrapper">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="2" animationDuration="3s" />
        </div>
      ) : showEspTable ? (
        <EspLogsTable contentProps={contentProps} />
      ) : dataLoaded && userPosts.length > 0 ? (
        <DataTable
          value={userPosts}
          totalRecords={totalRecords}
          onPage={onPage}
          expandedRows={expandedRows}
          onRowToggle={(e) => {
            setExpandedRows(e.data);
          }}
          rowExpansionTemplate={(data) => (
            <RowExpansionTemplate
              data={data}
              onRowCopy={onRowCopy}
              handleRowEdit={handleRowEdit}
              onRowDelete={onRowDelete}
              issueClass={issueClass}
            />
          )}
          stripedRows
          paginator
          header={header}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} logs"
          rows={recordsPerPage}
          first={currentPage * recordsPerPage}
          removableSort
        >
          <Column expander style={{ width: '5rem' }} />
          <Column
            header="Status"
            body={(rowData) => {
              const isComplete = personType === 'normal' ? rowData.hours >= 8 : rowData.hours >= 7;
              const statusClass = isComplete ? 'green-row' : 'red-row';
              return (
                <div className="status-btn-wrapper">
                  <div className={`status-indicator ${statusClass}`}></div>
                </div>
              );
            }}
            style={{ width: '2rem' }}
          ></Column>
          <Column field="date" header="Date"></Column>
          <Column field="workingh" header="Work hours"></Column>
          <Column field="wpl" header="Work place"></Column>
          <Column
            field="hours"
            header="Hours"
            body={(rowData) => {
              const isComplete = personType === 'normal' ? rowData.hours >= 8 : rowData.hours >= 7;
              const hourColorClass = isComplete ? 'hour-text-green' : 'hour-text-red';

              return <span className={`hour-font-weight ${hourColorClass}`}>{rowData.hours}</span>;
            }}
          ></Column>
          <Column field="cw" header="CW"></Column>
        </DataTable>
      ) : (
        <Message severity="info" text={dictionary.user_logs_table_no_posts} />
      )}
      {activeTab === 'esp' && !userIsAdmin ? (
        <EspAccess postTab={POST_TAB} setActiveTab={setActiveTab} setShowEspTable={setShowEspTable} />
      ) : null}
      {connectionError ? <Message severity="error" text={connectionError} /> : null}
      {forbiddenError ? <Message severity="warn" text={forbiddenError} /> : null}
    </div>
  );
};

export default UserLogsTable;
